/**
 * An event's photo gallery — TROOP-8, §6.
 *
 * **Two doors, one shape.** A member reaches the gallery under `/units/{unitId}/events/…` and a
 * guardian under `/family/events/…`, because a guardian holds no membership and `UnitScopeMiddleware`
 * 404s them before any `/units/` route runs. The difference is the path and nothing else — the same
 * payload, the same abilities, the same policy on the server (rule 13b). So every function here
 * takes an optional `unitId`: present means the member's door, absent means the family's.
 *
 * The downsize is why uploads go through `uploadEventPhoto` rather than a raw `fetch`: skipping it
 * sends an 8 MB phone photo over a campsite's signal, and because the canvas re-encode destroys the
 * EXIF locally, forgetting `capturedAt` silently loses the date on every photo. The server strips
 * and re-encodes regardless — nothing here is a security measure.
 */

import { API_BASE_URL, ApiError } from './client';
import { downsizeImage } from '../lib/imageDownsize';

export interface EventPhoto {
  id: string;
  storedFileId: string;
  caption: string | null;
  /** Short-lived. Refresh against `expiresAt` rather than discovering it as a broken image. */
  url: string;
  thumbnailUrl: string | null;
  addedByDisplayName: string;
  addedAt: string;
  /** From EXIF, or what the client read before its own downsize destroyed it. A label. */
  capturedAt: string | null;
  width: number | null;
  height: number | null;
  expiresAt: string;
  /** The uploader, and adult officers. Asked per row because it is a policy answer per row. */
  canRemove: boolean;
}

/**
 * Thrown when the unit has turned photos off (§6). Distinct from a 404 on purpose: this is a
 * leadership decision the viewer cannot act on, so the screen explains it rather than pretending
 * the gallery is empty.
 */
export class PhotosDisabledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhotosDisabledError';
  }
}

/**
 * Thrown when photos are on and this unit does not let this person add them (TROOP-225, §6).
 *
 * **Its own error, not a flavour of `PhotosDisabledError`.** A closed gallery and one somebody else
 * fills are different states and only one of them still shows the photographs, so the screens say
 * different things about them. Reaching this at all is a race — the control is not drawn for
 * somebody who cannot add — but the setting can narrow while a sheet is open, and a silent failure
 * there is worse than a sentence.
 */
export class PhotoUploadRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhotoUploadRefusedError';
  }
}

/**
 * Turns a 403 into whichever of the two it is. The server's `problem` code is what distinguishes
 * them; an unrecognised one falls back to the switch, which is the older and broader of the two.
 */
async function refusal(response: Response, fallback: string): Promise<Error> {
  const detail = await response.json().catch(() => null);
  const message = detail?.message ?? fallback;

  return detail?.problem === 'UploadNotAllowed'
    ? new PhotoUploadRefusedError(message)
    : new PhotosDisabledError(message);
}

const base = (unitId: string | undefined, eventId: string) =>
  unitId
    ? `${API_BASE_URL}/units/${unitId}/events/${eventId}/photos`
    : `${API_BASE_URL}/family/events/${eventId}/photos`;

/** The gallery. Returns null when there is no such event, or none this caller may read. */
export async function listEventPhotos(
  unitId: string | undefined,
  eventId: string,
): Promise<EventPhoto[] | null> {
  const response = await fetch(base(unitId, eventId), { credentials: 'include' });

  if (response.status === 404) return null;

  // Only ever the switch on a read: narrowing who may add never narrows who may see (TROOP-225).
  if (response.status === 403) {
    throw await refusal(response, 'This unit has turned photos off.');
  }

  if (!response.ok) {
    throw new ApiError(response.status, `GET photos for ${eventId} failed`);
  }

  return (await response.json()) as EventPhoto[];
}

export async function uploadEventPhoto(
  unitId: string | undefined,
  eventId: string,
  file: File,
  caption?: string,
): Promise<EventPhoto> {
  const { blob, capturedAt } = await downsizeImage(file);

  const form = new FormData();
  form.append('file', blob, file.name);

  if (capturedAt) form.append('capturedAt', capturedAt);
  if (caption?.trim()) form.append('caption', caption.trim());

  // No Content-Type header: the browser sets it, and it has to include the multipart boundary it
  // generated. Setting it by hand produces a body the server cannot parse.
  const response = await fetch(base(unitId, eventId), {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  // Two ways to be refused here, and the caller renders a different sentence for each.
  if (response.status === 403) {
    throw await refusal(response, 'This unit has turned photos off.');
  }

  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new ApiError(response.status, detail?.message ?? 'That photo could not be added.');
  }

  return (await response.json()) as EventPhoto;
}

/** A takedown. The row tombstones and the bytes stay — purging them is a separate, admin act. */
export async function removeEventPhoto(
  unitId: string | undefined,
  eventId: string,
  photoId: string,
): Promise<void> {
  const response = await fetch(`${base(unitId, eventId)}/${photoId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  // 404 covers both "no such photo" and "not yours", deliberately indistinguishable.
  if (!response.ok && response.status !== 204) {
    throw new ApiError(response.status, 'That photo could not be removed.');
  }
}

/**
 * One event on the photo feed, and the strip of photographs drawn under it — TROOP-119.
 *
 * `photoCount` is the whole gallery and `photos` is what the feed drew. They differ on purpose:
 * that difference is what the *see the rest* link is made of, and a screen that rendered every row
 * it was given is a bug the dev database has demonstrated repeatedly.
 */
export interface GalleryEvent {
  eventId: string;
  title: string;
  startsAt: string;
  /** Already resolved — the event's own zone, else its unit's. Never empty, never the reader's. */
  timeZoneId: string;
  eventTypeName: string | null;
  eventTypeColorKey: string;
  status: string;
  cancellationReason: string | null;
  unitId: string;
  /** Only earns its place on a parent's feed, which spans every unit their children are in. */
  unitName: string;
  photoCount: number;
  photos: EventPhoto[];
  /**
   * Whether this reader may add to *this* event's gallery — TROOP-225.
   *
   * **Per row, because a parent's feed spans units.** A child in a troop that photographs
   * everything beside one in a pack that has narrowed it gets a control on one section and not the
   * other, and a single answer for the page would be wrong for one of them.
   */
  canAdd: boolean;
}

/** A page of the feed. `nextCursor` is null at the beginning of the unit's history. */
export interface GalleryFeed {
  events: GalleryEvent[];
  nextCursor: string | null;
}

/**
 * A page of the photo feed, newest event first, walking backwards.
 *
 * The same two doors every photo call here uses: `unitId` present is the member's, absent is the
 * guardian's. Nothing else differs — the cursor is opaque and is only ever echoed back, so a
 * client holding one from an older shape restarts the walk rather than skipping a season.
 */
export async function listPhotoFeed(
  unitId: string | undefined,
  cursor?: string,
): Promise<GalleryFeed> {
  const path = unitId ? `${API_BASE_URL}/units/${unitId}/photos` : `${API_BASE_URL}/family/photos`;
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';

  const response = await fetch(`${path}${query}`, { credentials: 'include' });

  if (response.status === 403) {
    throw await refusal(response, 'This unit has turned photos off.');
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'The photos could not be loaded.');
  }

  return (await response.json()) as GalleryFeed;
}
